import React from 'react';

import { Tooltip } from '@mui/material';

import { FraudScoreDetails } from './FraudScoreDetails';
import { SingleCarHireDetails } from './SingleCarHireDetails';
import { SingleExperienceDetails } from './SingleExperienceDetails';
import { SingleFlightDetails } from './SingleFlightDetails';
import { SingleGiftCardDetails } from './SingleGiftCardDetails';
import { SingleHotelDetails } from './SingleHotelDetails';
import { SingleOrderPayments } from './SingleOrderPayments';
import { truncateOrderId } from './utils';

interface Props {
  order: App.Order;
}

function SingleOrder({ order }: Props) {
  const formattedTotal = order.total.toLocaleString(undefined, { maximumFractionDigits: 0 });

  if (order.has_bedbank) {
    const firstBedbankItem = order.bedbank_items[0];
    const address = firstBedbankItem.offer.address;
    const firstRoom = firstBedbankItem.rooms[0];

    return (
      <SingleHotelDetails
        order={order}
        checkIn={firstBedbankItem.check_in}
        bookingEmail={firstBedbankItem.booking_email}
        bookingPhone={firstBedbankItem.booking_phone}
        locationInfo={`Hotel in ${address.city}, ${address.countryName}`}
        guestFirstName={firstRoom.guest_first_name}
        guestLastName={firstRoom.guest_last_name}
        countryName={address.countryName}
      />
    );
  }

  if (order.accommodation_items.length > 0) {
    const firstHotelItem = order.accommodation_items[0];
    const reservation = firstHotelItem.reservation;
    let pendingBNBL = false;
    if (firstHotelItem?.reservation_type === 'buy_now_book_later' && firstHotelItem?.reservation_made === false) {
      pendingBNBL = true;
    }

    return (
      <SingleHotelDetails
        order={order}
        checkIn={reservation?.check_in}
        bookingEmail={reservation?.guest_email}
        bookingPhone={reservation?.guest_phone}
        locationInfo={firstHotelItem.offer.name}
        guestFirstName={reservation?.guest_first_name}
        guestLastName={reservation?.guest_last_name}
        pendingBNBL={pendingBNBL}
      />
    );
  }

  if (order.has_flight && order.flight_items.length > 0) {
    return <SingleFlightDetails order={order} />;
  }

  // Check for standalone experience orders (not attached to accommodation)
  if (order.has_experience && order.experience_items.length > 0) {
    const hasStandaloneExperiences = order.experience_items.some((item) => !item.complimentary);
    const hasNoAccommodation = !order.accommodation_items?.length && !order.bedbank_items?.length;

    if (hasStandaloneExperiences && hasNoAccommodation) {
      return <SingleExperienceDetails order={order} />;
    }
  }

  // Check for car hire orders
  if (order.car_hire_items?.length > 0) {
    return <SingleCarHireDetails order={order} />;
  }

  let mainType = 'unhandled type';
  if (order.tour_items.length > 0 || order.cruise_items.length > 0) {
    if (order.tour_items.length > 0) {
      mainType = 'tour';
    } else {
      mainType = 'cruise';
    }
    return (
      <div>
        Order {truncateOrderId(order.id)}{' '}
        <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
          {mainType}
        </a>{' '}
        for {order.currency_code}
        {formattedTotal}{' '}
        {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}{' '}
        <Tooltip title="Very unlikely a fraudster would book a tour or cruise">
          <span>🟢</span>
        </Tooltip>
        <br />
        {order.customer_full_name} {order.customer_email}
        <br />
        <FraudScoreDetails order={order} />
        <SingleOrderPayments order={order} />
      </div>
    );
  } else if (order.has_gift_card) {
    return <SingleGiftCardDetails order={order} />;
  } else {
    // Generic handling for all item types ending in _items
    const itemKeys = Object.keys(order).filter((k) => k.endsWith('_items'));
    const firstNonEmptyItem = itemKeys.find((k) => order[k] && order[k].length > 0);

    if (firstNonEmptyItem) {
      // Convert "my_type_items" to "my_type"
      mainType = firstNonEmptyItem.replace('_items', '');
    }
  }

  return (
    <div>
      Order {truncateOrderId(order.id)}{' '}
      <a target="_blank" href={`/purchases/${order.id}`} rel="noreferrer">
        {mainType}
      </a>{' '}
      for {order.currency_code}
      {formattedTotal}{' '}
      {order.fk_purchaser_id && order.fk_purchaser_id !== order.fk_customer_id && <strong>Spoofed Order</strong>}
      <br />
      {order.customer_full_name} {order.customer_email}
      <br />
      <FraudScoreDetails order={order} />
      <SingleOrderPayments order={order} />
    </div>
  );
}

export { SingleOrder };
